import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Clock, Users, Utensils } from "lucide-react";
import { SavedRecipe } from "@/hooks/useSavedRecipes";
import { NutritionIcons } from "./NutritionIcons";

interface RecipeDetailModalProps {
  recipe: SavedRecipe | null;
  isOpen: boolean;
  onClose: () => void;
}

export const RecipeDetailModal = ({ recipe, isOpen, onClose }: RecipeDetailModalProps) => {
  if (!recipe) return null;

  const totalTime = (recipe.prep_time_minutes || 0) + (recipe.cook_time_minutes || 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0">
        <ScrollArea className="max-h-[90vh]">
          <div className="p-6">
            <DialogHeader className="mb-4">
              <DialogTitle className="text-2xl font-semibold leading-tight pr-8">
                {recipe.title}
              </DialogTitle>
              {recipe.description && (
                <p className="text-muted-foreground mt-2">{recipe.description}</p>
              )}
            </DialogHeader>

            {/* Recipe Meta Info */}
            <div className="flex flex-wrap gap-4 mb-6">
              {totalTime > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-primary" />
                  <div>
                    <p className="font-medium">Total Time</p>
                    <p className="text-muted-foreground">{totalTime} min</p>
                  </div>
                </div>
              )}
              {recipe.prep_time_minutes && recipe.prep_time_minutes > 0 && (
                <div className="text-sm">
                  <p className="font-medium">Prep</p>
                  <p className="text-muted-foreground">{recipe.prep_time_minutes} min</p>
                </div>
              )}
              {recipe.cook_time_minutes && recipe.cook_time_minutes > 0 && (
                <div className="text-sm">
                  <p className="font-medium">Cook</p>
                  <p className="text-muted-foreground">{recipe.cook_time_minutes} min</p>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-primary" />
                <div>
                  <p className="font-medium">Servings</p>
                  <p className="text-muted-foreground">{recipe.servings}</p>
                </div>
              </div>
            </div>

            {/* Nutrition */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3">Nutrition (per serving)</h3>
              <div className="p-4 bg-gradient-card rounded-lg">
                <NutritionIcons
                  calories={recipe.calories}
                  protein={recipe.protein}
                  carbs={recipe.carbs}
                  fat={recipe.fat}
                  className="flex-wrap gap-4"
                  iconSize={18}
                />
                {recipe.fiber && recipe.fiber > 0 && (
                  <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 text-sm">
                    <span className="font-medium">Fiber:</span>
                    <span className="text-muted-foreground">{recipe.fiber}g</span>
                  </div>
                )}
              </div>
            </div>

            <Separator className="my-6" />

            {/* Equipment */}
            {recipe.equipment && recipe.equipment.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Utensils className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Equipment</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recipe.equipment.map((item, index) => (
                    <Badge key={index} variant="secondary">
                      {item}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Ingredients */}
            {recipe.ingredients && recipe.ingredients.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold mb-3">Ingredients</h3>
                <ul className="space-y-2">
                  {recipe.ingredients.map((ingredient) => (
                    <li key={ingredient.id} className="flex items-start gap-2 text-sm">
                      <span className="text-primary mt-1">•</span>
                      <span>
                        <strong>{ingredient.quantity} {ingredient.unit}</strong> {ingredient.ingredient_name}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Separator className="my-6" />

            {/* Instructions */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold mb-3">Instructions</h3>
              <div className="prose prose-sm max-w-none text-foreground">
                {recipe.instructions.split('\n').map((line, index) => {
                  const trimmed = line.trim();
                  if (!trimmed) return null;
                  
                  // Check if line starts with a number (step)
                  const stepMatch = trimmed.match(/^(\d+)\.\s*(.+)$/);
                  if (stepMatch) {
                    return (
                      <div key={index} className="mb-3 flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                          {stepMatch[1]}
                        </span>
                        <p className="flex-1 leading-relaxed">{stepMatch[2]}</p>
                      </div>
                    );
                  }
                  
                  return (
                    <p key={index} className="mb-2 leading-relaxed">
                      {trimmed}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
